import type { StrategyEntry } from "../services/investorStrategyService";

export interface StrategyTotals {
  allocatedPct: number;
  allocatedDollars: number | null;
  currentPct: number;
  currentDollars: number | null;
  capacityPct: number;
  remainingAllocatedPct: number;
  remainingAllocatedDollars: number | null;
  remainingCurrentPct: number;
  remainingCurrentDollars: number | null;
  isOverAllocated: boolean;
  isOverCurrent: boolean;
}

function absWeight(value: string): number {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? Math.abs(parsed) : 0;
}

function absDriftedOrWeight(entry: Pick<StrategyEntry, "weight" | "driftedWeight">): number {
  if (entry.driftedWeight != null && Number.isFinite(entry.driftedWeight)) {
    return Math.abs(entry.driftedWeight);
  }
  return absWeight(entry.weight);
}

export function computeStrategyTotals(
  strategies: ReadonlyArray<Pick<StrategyEntry, "weight" | "driftedWeight">>,
  capacityPct: number,
  nlv: number | null
): StrategyTotals {
  const allocatedPct = strategies.reduce((sum, e) => sum + absWeight(e.weight), 0);
  const currentPct = strategies.reduce((sum, e) => sum + absDriftedOrWeight(e), 0);

  const allocatedDollars = nlv != null ? (allocatedPct / 100) * nlv : null;
  const currentDollars = nlv != null ? (currentPct / 100) * nlv : null;

  const remainingAllocatedPct = capacityPct - allocatedPct;
  const remainingCurrentPct = capacityPct - currentPct;

  const remainingAllocatedDollars = nlv != null ? (remainingAllocatedPct / 100) * nlv : null;
  const remainingCurrentDollars = nlv != null ? (remainingCurrentPct / 100) * nlv : null;

  return {
    allocatedPct,
    allocatedDollars,
    currentPct,
    currentDollars,
    capacityPct,
    remainingAllocatedPct,
    remainingAllocatedDollars,
    remainingCurrentPct,
    remainingCurrentDollars,
    isOverAllocated: remainingAllocatedPct < 0,
    isOverCurrent: remainingCurrentPct < 0,
  };
}
